/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.20.1
 */

import type { IOptionItemProps } from '@components/common/Select/Select';

import { ECreateInquiryFieldType as Type } from './constants';
import type {
    ICreateInquiryAttribute,
    ICreateInquiryChangeAttribute,
    ICreateInquiryInitialValues,
    IInquirySectionAndTopics,
    TCreateInquiryAttributeValue,
} from './types';

/*
 * -------------------------------------------
 *           Section and Topics
 * -------------------------------------------
 */

export interface IInquiryTopic {
    code: string;
    def: string;
}

export interface IInquirySection extends IInquiryTopic {
    topics: IInquiryTopic[];
}

export interface IInquiryTopicsResponse {
    sections: IInquirySection[];
    userInfo: {
        id: number;
        label: string;
        name: string;
    };
    contactInfo?: {
        email?: string;
        phone?: string;
    };
}

type TSetInitialValues = (
    sections: IInquirySection[],
    initialTopicCode: string | undefined,
) => ICreateInquiryInitialValues | undefined;

const setInitialValues: TSetInitialValues = (sections, initialTopicCode) => {
    let initialValues: ICreateInquiryInitialValues | undefined;

    if (!initialTopicCode) {
        return initialValues;
    }

    sections.forEach((section) => {
        const initialTopic = section.topics.find((topic) => topic.code === initialTopicCode);

        if (!!initialTopic) {
            const sectionOption: IOptionItemProps = {
                label: section.def,
                value: section.code,
            };

            const topicOption: IOptionItemProps = {
                label: initialTopic.def,
                value: initialTopic.code,
            };

            initialValues = {
                section: sectionOption,
                topic: topicOption,
            };
        }
    });

    return initialValues;
};

export const mapInquirySectionAndTopics = (response: IInquiryTopicsResponse, initialTopicCode?: string) => {
    const topics: IInquirySectionAndTopics['topics'] = {};
    const sections: IInquirySectionAndTopics['sections'] = [];

    response.sections.forEach((item) => {
        if (item.code && item.def) {
            sections.push({ label: item.def, value: item.code });
            topics[item.code] = item.topics.map((topic) => ({ label: topic.def, value: topic.code }));
        }
    });

    return {
        sections,
        topics,
        userInfo: response.userInfo,
        contactInfo: response.contactInfo,
        initialValues: setInitialValues(response.sections, initialTopicCode),
    };
};

/*
 * -------------------------------------------
 *           Change Master Attribute
 * -------------------------------------------
 */

export const onChangeAttributeGetNewSlaveAttributes = (
    topicCode: string,
    data: ICreateInquiryChangeAttribute,
): { [attributeId: string]: ICreateInquiryAttribute } => {
    if (data.customAttrs) {
        return (
            data.customAttrs?.reduce((slaveAttrs: { [attrId: string]: ICreateInquiryAttribute }, attr) => {
                slaveAttrs[attr.code] = attr;
                return slaveAttrs;
            }, {}) ?? {}
        );
    }

    return {};
};

/*
 * при изменении мастер атрибута, в ответ приходят slave атрибуты,
 * которые зависят от переданного мастер атрибута
 * новые значения slave атрибутов, заменяют полученные ранее
 */

export const onChangeAttributeMergeAttributesWithNew =
    (newSlaveAttributes: { [attributeId: string]: ICreateInquiryAttribute }) => (attr: ICreateInquiryAttribute) =>
        newSlaveAttributes[attr.code]
            ? {
                  ...attr,
                  declaration: {
                      ...attr.declaration,
                      ...newSlaveAttributes[attr.code].declaration,
                  },
              }
            : attr;

/*
 * -------------------------------------------
 *     Map UI attributes to API Configuration
 * -------------------------------------------
 */

type TInquiryValues = Required<ICreateInquiryAttribute['declaration']>['values'];

const mapCustomAttributeDictionaryValues = (values?: IOptionItemProps[]): TInquiryValues =>
    values?.reduce((items: TInquiryValues, item: IOptionItemProps) => {
        if (item?.value && item?.label) {
            items.push({ id: item.value, name: item.label });
        }
        return items;
    }, []) ?? [];

export const mapCustomAttributeValues =
    (customAttrsValues: { [code: string]: any }) =>
    (attribute: ICreateInquiryAttribute): TCreateInquiryAttributeValue | null => {
    if (!customAttrsValues[attribute.code]) return null
    return {
        code: attribute.code,
        declaration: {
            type: attribute.declaration.type,
            accessibility: attribute.declaration.accessibility,
            ...{
                [Type.string]: { stringValue: customAttrsValues[attribute.code] },
                [Type.number]: { numberValue: customAttrsValues[attribute.code] },
                [Type.date]: { dateValue: customAttrsValues[attribute.code] },
                [Type.bool]: { booleanValue: customAttrsValues[attribute.code] },
                [Type.dictionary]: {
                    values: attribute.declaration.multiSelect
                        ? mapCustomAttributeDictionaryValues(customAttrsValues[attribute.code])
                        : mapCustomAttributeDictionaryValues([customAttrsValues[attribute.code]]),
                },
                [Type.dbQuery]: { values: attribute.declaration.values },
            }[attribute.declaration.type],
        },
    }};

export const mapCurrentMasterAttributeValuesToApi = (
    attribute: ICreateInquiryAttribute,
): TCreateInquiryAttributeValue => ({
    code: attribute.code,
    declaration: {
        type: attribute.declaration.type,
        accessibility: attribute.declaration.accessibility,
        ...{
            [Type.string]: {},
            [Type.number]: {},
            [Type.date]: {},
            [Type.dbQuery]: {},

            // master attribute is type dictionary or bool only
            [Type.bool]: { booleanValue: !!attribute.declaration?.booleanValue },
            [Type.dictionary]: {
                values: attribute.declaration?.values || [],
            },
        }[attribute.declaration.type],
    },
});
